import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { useToast, Spinner, ErrorBanner, Badge, Modal, EmptyState, ConfirmDialog } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

function CreateUserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ fullName:'', email:'', role:'staff', temporaryPassword:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/api/users', form); onSaved('User created — welcome email sent'); onClose(); }
    catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New User" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="user-create-form" type="submit">{saving?'Creating…':'Create user'}</button></>}>
      <form id="user-create-form" onSubmit={save}>
        <div className="form-group"><label>Full name *</label><input required value={form.fullName} onChange={e=>set('fullName',e.target.value)} /></div>
        <div className="form-group"><label>Email *</label><input type="email" required value={form.email} onChange={e=>set('email',e.target.value)} /></div>
        <div className="form-group"><label>Role</label>
          <select value={form.role} onChange={e=>set('role',e.target.value)}>
            <option value="read_only">Read Only</option>
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-group">
          <label>Temporary password *</label>
          <input type="password" required minLength={8} value={form.temporaryPassword} onChange={e=>set('temporaryPassword',e.target.value)} />
          <small style={{ fontSize:11, color:'var(--text-tertiary)' }}>Min 8 characters. User will receive a welcome email and be prompted to change on first login.</small>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ fullName: user.full_name, email: user.email || '', phone: user.phone || '', role: user.role });
  const [resetPw, setResetPw] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/api/users/${user.id}`, form);
      if (resetPw && newPassword) {
        await api.post(`/api/users/${user.id}/reset-password`, { temporaryPassword: newPassword });
      }
      onSaved('User updated successfully'); onClose();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit — ${user.full_name}`} onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="user-edit-form" type="submit">{saving?'Saving…':'Save changes'}</button></>}>
      <form id="user-edit-form" onSubmit={save}>
        <div className="form-group"><label>Full name *</label><input required value={form.fullName} onChange={e=>set('fullName',e.target.value)} /></div>
        <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e=>set('email',e.target.value)} /></div>
        <div className="form-group"><label>Phone</label><input type="tel" placeholder="e.g. (330) 730-4698" value={form.phone} onChange={e=>set('phone',e.target.value)} /></div>
        <div className="form-group"><label>Role</label>
          <select value={form.role} onChange={e=>set('role',e.target.value)}>
            <option value="read_only">Read Only</option>
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div style={{ borderTop:'0.5px solid var(--card-border)', marginTop:16, paddingTop:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: resetPw ? 12 : 0 }}>
            <input type="checkbox" id="reset-pw" style={{ width:'auto' }} checked={resetPw} onChange={e=>setResetPw(e.target.checked)} />
            <label htmlFor="reset-pw" style={{ marginBottom:0, cursor:'pointer' }}>Reset password</label>
          </div>
          {resetPw && (
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>New temporary password *</label>
              <input type="password" required={resetPw} minLength={8} value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
              <small style={{ fontSize:11, color:'var(--text-tertiary)' }}>User will receive an email with the new password and be prompted to change it on login.</small>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default function Users() {
  const { isAdmin, user: me } = useAuth();
  const { data, loading, error, refetch } = useFetch('/api/users');
  const { showToast, Toast } = useToast();
  const [modal, setModal] = useState(null);  // 'create' | {user object for edit}
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const toggleActive = async (u) => {
    try { await api.patch(`/api/users/${u.id}`, { active: !u.active }); refetch(); showToast(u.active ? 'User deactivated' : 'User activated'); }
    catch (err) { alert(errMsg(err)); }
  };

  const deleteUser = async (u) => {
    try { await api.delete(`/api/users/${u.id}`); refetch(); showToast('User deleted'); }
    catch (err) { alert(errMsg(err)); }
    finally { setDeleteConfirm(null); }
  };

  if (!isAdmin) return <div className="alert alert-info" style={{ marginTop:16 }}><i className="ti ti-lock" /> Admin access required.</div>;
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  return (
    <div>

      {modal === 'create' && (
        <CreateUserModal onClose={() => setModal(null)} onSaved={(msg) => { refetch(); showToast(msg || 'User created'); }} />
      )}
      {modal && modal !== 'create' && (
        <EditUserModal user={modal} onClose={() => setModal(null)} onSaved={(msg) => { refetch(); showToast(msg || 'User updated'); }} />
      )}
      {deleteConfirm && (
        <ConfirmDialog
          danger
          message={`Permanently delete ${deleteConfirm.full_name}? This cannot be undone.`}
          onConfirm={() => deleteUser(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      <div className="page-header">
        <h1>Users</h1>
        <div className="page-actions">
          <button className="btn btn-primary btn-sm" onClick={() => setModal('create')}>
            <i className="ti ti-plus" /> New user
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last login</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {(data||[]).map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight:500 }}>
                    {u.full_name}
                    {u.id === me?.id && <span style={{ fontSize:11, color:'var(--text-tertiary)', marginLeft:6 }}>(you)</span>}
                  </td>
                  <td style={{ fontSize:12 }}>{u.email}</td>
                  <td><Badge status={u.role} label={u.role.replace('_',' ')} /></td>
                  <td><Badge status={u.active?'active':'cancelled'} label={u.active?'Active':'Inactive'} /></td>
                  <td style={{ fontSize:12 }}>{u.last_login_at ? fmt.dateTime(u.last_login_at) : '—'}</td>
                  <td>
                    <div style={{ display:'flex', gap:4 }}>
                      {/* Edit */}
                      <button className="btn btn-sm" title="Edit user" onClick={() => setModal(u)}>
                        <i className="ti ti-pencil" />
                      </button>
                      {/* Activate / Deactivate */}
                      {u.id !== me?.id && (
                        <button className="btn btn-sm" title={u.active?'Deactivate':'Activate'} onClick={() => toggleActive(u)}>
                          <i className={`ti ${u.active ? 'ti-user-off' : 'ti-user-check'}`} />
                        </button>
                      )}
                      {/* Delete */}
                      {u.id !== me?.id && (
                        <button className="btn btn-sm btn-danger" title="Delete user" onClick={() => setDeleteConfirm(u)}>
                          <i className="ti ti-trash" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!(data||[]).length && <EmptyState icon="ti-users" message="No users found" />}
        </div>
      </div>
    </div>
  );
}
